import { FC } from "react";
import { Button, Typography, Card, CardContent, CardHeader, Checkbox } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import type { Criterion } from "../types";

interface CriteriaSelectorProps {
  criteria: Criterion[];
  selectedCriteria: string[];
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const CriteriaSelector: FC<CriteriaSelectorProps> = ({
  criteria,
  selectedCriteria,
  onToggle,
  onSelectAll,
  onDeselectAll,
}) => {
  return (
    <Card>
      <CardHeader>
        <Stack direction="row" justify="between" align="center">
          <Typography size="regular" weight="medium">
            Criteria ({selectedCriteria.length}/{criteria.length})
          </Typography>
          <Stack direction="row" gap="xs">
            <Button onClick={onSelectAll} variant="ghost" size="sm" className="al-text-xs">
              Select All
            </Button>
            <Button onClick={onDeselectAll} variant="ghost" size="sm" className="al-text-xs">
              Clear
            </Button>
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent className="al-max-h-96 al-overflow-y-auto">
        {criteria.length === 0 ? (
          <div className="al-text-center al-py-8 al-text-sm al-text-gray-500">
            No criteria available. Create criteria in the Criteria tab.
          </div>
        ) : (
          <div className="al-space-y-2">
            {criteria.map(criterion => (
              <div
                key={criterion.id}
                role="button"
                tabIndex={0}
                className={`al-p-3 al-border al-rounded-md al-cursor-pointer al-transition-colors ${
                  selectedCriteria.includes(criterion.id)
                    ? "al-bg-purple-50 al-border-purple-300"
                    : "al-bg-white al-border-gray-200 hover:al-border-gray-300"
                }`}
                onClick={() => onToggle(criterion.id)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggle(criterion.id);
                  }
                }}
              >
                <Stack direction="row" align="start" gap="sm">
                  <Checkbox
                    checked={selectedCriteria.includes(criterion.id)}
                    onCheckedChange={() => onToggle(criterion.id)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    className="al-mt-0.5"
                  />
                  <div className="al-flex-1">
                    <Typography size="sm" weight="medium" className="al-mb-1">
                      {criterion.name}
                    </Typography>
                    <Typography size="xs" className="al-text-gray-600 al-line-clamp-2">
                      {criterion.description}
                    </Typography>
                  </div>
                </Stack>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
